import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Newspaper, Briefcase } from "lucide-react";
import { fetchBlogPosts, fetchKnowledgeHub, fetchServices } from "@/lib/content";

type Props = {
  /** Slug of the currently viewed article to exclude from the related list. */
  currentSlug?: string;
  /** Which section the current article belongs to. */
  source: "blog" | "knowledge-hub";
};

/**
 * Renders a "Recommended Reading" block that always links out to
 * other blog posts, knowledge hub articles and a featured service page,
 * forming a coherent internal-link web for crawlers.
 */
export function RelatedArticles({ currentSlug, source }: Props) {
  const { data: blogPosts = [] } = useQuery({ queryKey: ["blog_posts"], queryFn: fetchBlogPosts });
  const { data: hubPosts = [] } = useQuery({ queryKey: ["knowledge_hub"], queryFn: fetchKnowledgeHub });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: fetchServices });

  const otherBlogs = (blogPosts as any[])
    .filter((p) => p.slug !== currentSlug || source !== "blog")
    .slice(0, 3);
  const otherHub = (hubPosts as any[])
    .filter((p) => p.slug !== currentSlug || source !== "knowledge-hub")
    .slice(0, 3);
  const topServices = (services as any[]).slice(0, 4);

  if (otherBlogs.length + otherHub.length + topServices.length === 0) return null;

  return (
    <aside className="mt-16 pt-10 border-t border-emerald-700/20">
      <div className="flex items-center gap-3 mb-6">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
          <BookOpen className="h-4 w-4" />
        </span>
        <h2 className="font-display text-2xl font-bold tracking-tight">Recommended Reading</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {otherHub.length > 0 && (
          <section className="rounded-2xl border border-emerald-700/30 bg-[#0f5132] text-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3 text-emerald-100">
              <BookOpen className="h-4 w-4" />
              <h3 className="text-sm font-semibold uppercase tracking-wider">From the Knowledge Hub</h3>
            </div>
            <ul className="space-y-2.5">
              {otherHub.map((p: any) => (
                <li key={p.slug}>
                  <Link
                    to="/knowledge-hub/$slug"
                    params={{ slug: p.slug }}
                    className="group flex items-start gap-2 text-sm hover:text-emerald-200 transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                    <span className="font-medium leading-snug">{p.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/knowledge-hub"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-100 hover:text-white"
            >
              Browse all articles <ArrowRight className="h-3 w-3" />
            </Link>
          </section>
        )}

        {otherBlogs.length > 0 && (
          <section className="rounded-2xl border border-primary/20 bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3 text-primary">
              <Newspaper className="h-4 w-4" />
              <h3 className="text-sm font-semibold uppercase tracking-wider">More from the Blog</h3>
            </div>
            <ul className="space-y-2.5">
              {otherBlogs.map((p: any) => (
                <li key={p.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="group flex items-start gap-2 text-sm hover:text-primary transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                    <span className="font-medium leading-snug">{p.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/blog"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              View all blog posts <ArrowRight className="h-3 w-3" />
            </Link>
          </section>
        )}
      </div>

      {topServices.length > 0 && (
        <section className="mt-6 rounded-2xl border bg-muted/40 p-5">
          <div className="flex items-center gap-2 mb-3 text-foreground">
            <Briefcase className="h-4 w-4" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Explore Our Services</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {topServices.map((s: any) => (
              <Link
                key={s.slug}
                to="/services"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-background px-3 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {s.title}
              </Link>
            ))}
            <Link
              to="/packages"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              View packages <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </section>
      )}
    </aside>
  );
}
